/**
 * Reason code dictionary + parser.
 *
 * The risk-scorer worker writes a `;`-joined string of up to 8 reasons
 * to AE blob15. Each reason is shaped:
 *
 *     <family>:<value>[:<+/-weight>]
 *
 * Examples emitted by `workers/risk-scorer/scoring/framework.js`:
 *
 *     bot_score:6:+10
 *     ja4_distinct_ips:92:+25
 *     ja4_failure_rate:0.97:+20
 *     ja4_signals.browser_ratio:0.04:+20
 *     managed_list:botnetcc:+30
 *     ip_distinct_emails:50:+30
 *     trust:known_country:-8
 *     verified_bot:exempt
 *     signal-missing:botScore
 *     cap-applied:detection_ids:120->60
 *
 * This module is the single source of truth that:
 *   1. Maps each reason family to a short label, plain-language sentence,
 *      and severity tone for chip rendering.
 *   2. Maps each Cloudflare managed-list name to a plain sentence.
 *   3. Provides `parseReasons()` so the UI never re-implements the split.
 *   4. Provides `summariseReasons()` so chips order by impact.
 *
 * Adding a new reason emitted by the scorer? Add a `REASON_DEFS` entry
 * here so it gets a chip; otherwise the chip falls back to a generic
 * neutral pill that still shows the raw code for engineers.
 */

export type ReasonSeverity = "high" | "suspicious" | "watch" | "good" | "neutral";

export interface ReasonDef {
  /** Short chip label (<= ~22 chars). */
  short: string;
  /** One-sentence plain explanation for the legend + popover. */
  plain: string;
  /** Tone hint for the chip background. */
  severity: ReasonSeverity;
  /** High-level grouping shown in the legend. */
  family: "Threat list" | "Bot signal" | "TLS fingerprint" | "Behaviour" | "App flow" | "Trust" | "Diagnostic";
}

export interface DetectionIdDef {
  short: string;
  plain: string;
  weight: number;
}

export const DETECTION_ID_DEFS: Record<string, DetectionIdDef> = {
  "201326592": {
    short: "Login failures",
    plain: "Cloudflare saw a suspicious burst of failed login traffic for this zone.",
    weight: 40,
  },
  "201326593": {
    short: "Login attempts",
    plain: "Cloudflare saw a suspicious burst of login attempts for this zone.",
    weight: 30,
  },
  "201326598": {
    short: "Zone traffic anomaly",
    plain: "Cloudflare's dynamic baseline for this zone was exceeded by unusual request volume or shape.",
    weight: 20,
  },
  "50331648": {
    short: "ASN anomaly",
    plain: "Cloudflare saw abnormal behavior from this source network compared with normal ASN traffic.",
    weight: 25,
  },
  "50331649": {
    short: "JA4 anomaly",
    plain: "Cloudflare saw abnormal behavior from requests sharing this JA4 TLS fingerprint.",
    weight: 45,
  },
};

function detectionReasonDef(id: string | null): ReasonDef {
  const detection = id ? DETECTION_ID_DEFS[id] : undefined;
  if (!detection || !id) return REASON_DEFS.detection_id;
  return {
    short: "Cloudflare alert",
    plain: `Cloudflare alert ${id}: ${detection.plain}`,
    severity: "high",
    family: "Bot signal",
  };
}

/**
 * Match by exact family key (e.g. "bot_score") OR by `family:value`
 * for narrowly-scoped reasons that need their own copy
 * (e.g. "trust:recent_mfa", "managed_list:botnetcc").
 */
export const REASON_DEFS: Record<string, ReasonDef> = {
  // --- Bot Manager -----------------------------------------------------
  bot_score: {
    short: "Bot-like traffic",
    plain: "Cloudflare Bot Management gave this request a low 'likely human' score - lower means more bot-like.",
    severity: "high",
    family: "Bot signal",
  },
  verified_bot: {
    short: "Verified bot",
    plain: "Cloudflare verified this as a known good bot (e.g. search engine). Score is dampened.",
    severity: "good",
    family: "Bot signal",
  },

  // --- Cloudflare detection IDs ---------------------------------------
  detection_id: {
    short: "Cloudflare alert",
    plain: "Cloudflare emitted a bot or traffic-shape alert for this request. The event details show the numeric ID, score contribution, and meaning when the ID is known.",
    severity: "high",
    family: "Bot signal",
  },

  // --- Managed lists (also see MANAGED_LIST_DEFS for per-list copy) ---
  managed_list: {
    short: "On a Cloudflare threat list",
    plain: "The source IP is on a Cloudflare-curated managed threat list (botnet C&C, malware, open proxies, anonymisers, or VPN).",
    severity: "high",
    family: "Threat list",
  },

  // --- JA4 behavioural windows ---------------------------------------
  ja4_distinct_ips: {
    short: "Same TLS across many IPs",
    plain: "This TLS fingerprint (JA4) was seen from many different IPs in the last hour - typical of distributed automation tooling.",
    severity: "high",
    family: "TLS fingerprint",
  },
  ja4_distinct_asns: {
    short: "Same TLS across many networks",
    plain: "This JA4 fingerprint spans many different ASNs in the last hour - unusual for genuine browser traffic.",
    severity: "suspicious",
    family: "TLS fingerprint",
  },
  ja4_failure_rate: {
    short: "TLS family failing often",
    plain: "Recent requests sharing this JA4 fingerprint failed at a high rate (login/checkout failures).",
    severity: "high",
    family: "TLS fingerprint",
  },

  // --- JA4 signals (Cloudflare's 1h aggregates) -----------------------
  "ja4_signals.browser_ratio": {
    short: "Doesn't look like a browser",
    plain: "Cloudflare's JA4 signals indicate this fingerprint rarely behaves like a real browser (low browser ratio).",
    severity: "high",
    family: "TLS fingerprint",
  },
  "ja4_signals.heuristic_ratio": {
    short: "Heuristic mismatch",
    plain: "Cloudflare's heuristics flagged this fingerprint as unusual versus its claimed user agent.",
    severity: "suspicious",
    family: "TLS fingerprint",
  },
  "ja4_signals.h2h3_ratio": {
    short: "Old TLS protocol",
    plain: "This JA4 fingerprint uses HTTP/1.1 disproportionately - modern browsers prefer HTTP/2 or HTTP/3.",
    severity: "watch",
    family: "TLS fingerprint",
  },
  "ja4_signals.uas_rank": {
    short: "Rare user agent",
    plain: "The user-agent string used with this JA4 is rare across Cloudflare's network.",
    severity: "suspicious",
    family: "TLS fingerprint",
  },
  "ja4_signals.paths_scanner": {
    short: "Scanner-like paths",
    plain: "This JA4 has touched an unusually wide set of paths recently - typical of vulnerability scanners.",
    severity: "high",
    family: "TLS fingerprint",
  },
  "ja4_signals.botnet_scale": {
    short: "Botnet-scale traffic",
    plain: "This JA4 is generating traffic at a scale typical of botnet activity.",
    severity: "high",
    family: "TLS fingerprint",
  },

  // --- IP behavioural window -----------------------------------------
  ip_distinct_emails: {
    short: "IP touching many accounts",
    plain: "This IP has attempted many different email addresses in the last hour - a credential-stuffing pattern.",
    severity: "high",
    family: "Behaviour",
  },
  ip_distinct_businesses: {
    short: "IP touching many businesses",
    plain: "This IP has touched many different business accounts in the last hour.",
    severity: "suspicious",
    family: "Behaviour",
  },
  ip_login_failures: {
    short: "IP failing logins",
    plain: "This IP has failed login multiple times in the last hour.",
    severity: "high",
    family: "Behaviour",
  },

  // --- ASN behavioural window ----------------------------------------
  asn_distinct_ips: {
    short: "Network has many active IPs",
    plain: "Many different IPs from this ASN have been active in the last hour.",
    severity: "watch",
    family: "Behaviour",
  },
  asn_failure_rate: {
    short: "Network failing often",
    plain: "Requests from this ASN are failing at a high rate (login or checkout failures).",
    severity: "suspicious",
    family: "Behaviour",
  },

  // --- App-flow signals ----------------------------------------------
  "app_flow:out_of_order_payment_step": {
    short: "Skipped payment step",
    plain: "The customer hit a later payment step without first hitting the expected earlier step.",
    severity: "high",
    family: "App flow",
  },
  "app_flow:cross_business_access": {
    short: "Cross-business access",
    plain: "The same session is touching more than one business account in a short window.",
    severity: "suspicious",
    family: "App flow",
  },
  "app_flow:sensitive_without_mfa": {
    short: "Sensitive action without MFA",
    plain: "A sensitive action (auth or payment) was attempted by a session that has not recently passed MFA.",
    severity: "suspicious",
    family: "App flow",
  },

  // --- Trust dampeners (negative weight = lower score) ---------------
  "trust:known_country": {
    short: "Known good country",
    plain: "This session has historically operated from this country - score is dampened.",
    severity: "good",
    family: "Trust",
  },
  "trust:known_asn": {
    short: "Known good network",
    plain: "This session has historically used this network - score is dampened.",
    severity: "good",
    family: "Trust",
  },
  "trust:known_ja4": {
    short: "Known good device",
    plain: "This session has historically used this TLS fingerprint - score is dampened.",
    severity: "good",
    family: "Trust",
  },
  "trust:known_email": {
    short: "Known good email",
    plain: "This session has historically used this email - score is dampened.",
    severity: "good",
    family: "Trust",
  },
  "trust:recent_mfa": {
    short: "Recent MFA pass",
    plain: "This session passed MFA recently - score is dampened.",
    severity: "good",
    family: "Trust",
  },
  "trust:shared_admin_dampener": {
    short: "Shared admin device",
    plain: "Detected as a likely shared-admin device pattern - score is halved to reduce false positives.",
    severity: "neutral",
    family: "Trust",
  },

  // --- Diagnostic / non-scoring --------------------------------------
  "signal-missing": {
    short: "Signal missing",
    plain: "A scoring input was unavailable for this request - useful when investigating false positives.",
    severity: "neutral",
    family: "Diagnostic",
  },
  "cap-applied": {
    short: "Score capped",
    plain: "The contribution from one category exceeded its cap and was clamped before being added to the final score.",
    severity: "neutral",
    family: "Diagnostic",
  },
};

/**
 * Plain-language descriptions for every Cloudflare managed list we
 * surface in the dashboard - used by both the Threat-list column and the
 * legend.
 */
export const MANAGED_LIST_DEFS: Record<string, { short: string; plain: string; severity: ReasonSeverity }> = {
  botnetcc: {
    short: "Botnet C&C",
    plain: "Source IP is on Cloudflare's Botnet Command-and-Control list - hosts known to control compromised devices.",
    severity: "high",
  },
  malware: {
    short: "Malware",
    plain: "Source IP is on Cloudflare's Malware list - known to host or distribute malware.",
    severity: "high",
  },
  "open-proxies": {
    short: "Open proxy",
    plain: "Source IP is on Cloudflare's Open Proxies list - traffic likely relayed through a publicly open proxy.",
    severity: "suspicious",
  },
  anonymizer: {
    short: "Anonymiser",
    plain: "Source IP is on Cloudflare's Anonymisers list - used to hide the originating client (e.g. Tor).",
    severity: "suspicious",
  },
  vpn: {
    short: "VPN",
    plain: "Source IP is on Cloudflare's VPN list - commercial VPN provider.",
    severity: "watch",
  },
};

export interface ParsedReason {
  /** Original raw string for engineers. */
  raw: string;
  /** Family key used to look up the def (e.g. "bot_score"). */
  code: string;
  /** Specific value seen (e.g. "92" for ja4_distinct_ips). */
  value: string | null;
  /** Numeric weight contribution (positive raises score, negative lowers). */
  weight: number;
  /** Definition matched (short, plain, severity, family) or fallback. */
  def: ReasonDef;
}

const FALLBACK_DEF: ReasonDef = {
  short: "Other signal",
  plain: "An additional scoring signal not yet documented in this legend.",
  severity: "neutral",
  family: "Diagnostic",
};

/**
 * Parse the `;`-joined reasons string written by the scorer into a list
 * of structured reasons. Returns an empty array if the input is missing
 * or empty.
 */
export function parseReasons(raw: string | null | undefined): ParsedReason[] {
  if (!raw) return [];
  const parts = String(raw).split(";").map((s) => s.trim()).filter(Boolean);
  const out: ParsedReason[] = [];
  for (const part of parts) {
    const segments = part.split(":");
    const family = segments[0] || part;
    const value = segments.length > 1 ? segments.slice(1, -1).join(":") || segments[1] || null : null;
    const weightToken = segments.length > 2 ? segments[segments.length - 1] : null;
    const weightMatch = weightToken && /^[+-]?\d+(?:\.\d+)?$/.test(weightToken)
      ? Number(weightToken)
      : 0;

    // Match by exact family:value first (e.g. "trust:known_country"),
    // then fall back to the family alone (e.g. "bot_score").
    const composite = value !== null ? `${family}:${value}` : family;
    const def = family === "detection_id"
      ? detectionReasonDef(value)
      : REASON_DEFS[composite] ?? REASON_DEFS[family] ?? FALLBACK_DEF;

    out.push({
      raw: part,
      code: family,
      value: value,
      weight: weightMatch,
      def,
    });
  }
  return out;
}

/**
 * Order reasons for chip display: highest absolute weight first, with
 * "Trust" dampeners pushed last regardless of weight (they explain why a
 * score isn't higher, not why it is).
 */
export function summariseReasons(parsed: ParsedReason[], top = 2): {
  top: ParsedReason[];
  remaining: number;
} {
  const sorted = [...parsed].sort((a, b) => {
    const trustA = a.def.family === "Trust" ? 1 : 0;
    const trustB = b.def.family === "Trust" ? 1 : 0;
    if (trustA !== trustB) return trustA - trustB;
    return Math.abs(b.weight) - Math.abs(a.weight);
  });
  return {
    top: sorted.slice(0, top),
    remaining: Math.max(0, sorted.length - top),
  };
}

/**
 * Plain copy used by ReasonsKey so non-technical readers can decode each
 * dashboard Area. Stored endpoint groups can map to the same Area.
 */
export const ENDPOINT_GROUP_DEFS: Record<string, { short: string; plain: string }> = {
  auth: {
    short: "Login",
    plain: "Login and authentication requests.",
  },
  sms: {
    short: "Messages (Email/SMS)",
    plain: "SMS-bearing message and verification requests. The dashboard combines this with email/message endpoints.",
  },
  register: {
    short: "Registration",
    plain: "New account registration on app.gettimely.com.",
  },
  payments: {
    short: "Payments",
    plain: "Payment-method onboarding, Stripe wiring, and online-booking payments.",
  },
  messaging: {
    short: "Messages (Email/SMS)",
    plain: "Outbound email and message endpoints. The dashboard combines this with SMS-bearing requests.",
  },
  bookings: {
    short: "Bookings",
    plain: "Public booking pages and time-slot lookups.",
  },
  gift_vouchers: {
    short: "Gift vouchers",
    plain: "Voucher PDF printing on gettimely.com.",
  },
  other: {
    short: "Other",
    plain: "Monitored endpoints that don't fit any of the named areas.",
  },
};
